import { useAppContext } from '../../../context/AppContext';
import { postData } from '../../../services/postData';
import { getUserRole, trackUserCreated } from '../../../services';
import { useState } from 'react';
import Modal from '../../helpers/Modal';

export const AddUserForm = ({ setUsers, onClose, companyId }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        job_title: '',
        phone: '',
        user_type: 'Standard',
        is_admin: false,
        is_super_admin: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const { token, base_url, user, company } = useAppContext();

    const userTypes = [
        'Standard',
        'Design Only'
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // If companyId is provided (super admin adding user to a specific company), include it
            const payload = companyId ? { ...formData, company_id: companyId } : formData;
            console.log('Adding user with data:', payload);
            const response = await postData(token, `${base_url}/apis/admin/users`, payload);

            if (response && response.user) {
                setUsers(prevUsers => [...prevUsers, response.user]);
                trackUserCreated({
                    companyId: companyId || response.user.company_id || company?.id || user?.company_id,
                    userId: response.user.id,
                    userRole: getUserRole(response.user),
                });
                window.showAlert(200, 'User added successfully!');
                console.log('User added successfully!');
                onClose();
            } else {
                setError(response?.msg || 'Failed to add user. Please try again.');
                window.showAlert(response.status, response?.msg || 'Failed to add user. Please try again.');
            }
        } catch (err) {
            setError('Failed to add user. Please try again.');
            console.error('Add user error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = (open) => {
        if (!open) onClose();
    };

    const addUserForm = (
        <div>
            <h2 className="modal-title modal-title--compact">Add New User</h2>

            {error && (
                <div className="form-error-banner">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="company-form-grid">
                <h3 className="form-section-heading">Personal Information</h3>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="first_name" className="form-label">
                            First Name *
                        </label>
                        <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            className="form-input"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter first name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="last_name" className="form-label">
                            Last Name *
                        </label>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            className="form-input"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter last name"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="email" className="form-label">
                            Email *
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-input"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter email address"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone" className="form-label">
                            Phone
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="form-input"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="job_title" className="form-label">
                            Job Title
                        </label>
                        <input
                            type="text"
                            id="job_title"
                            name="job_title"
                            className="form-input"
                            value={formData.job_title}
                            onChange={handleInputChange}
                            placeholder="e.g., Project Manager"
                        />
                    </div>
                </div>

                <h3 className="form-section-heading">User Permissions</h3>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="user_type" className="form-label">
                            User Type
                        </label>
                        <select
                            id="user_type"
                            name="user_type"
                            className="form-select"
                            value={formData.user_type}
                            onChange={handleInputChange}
                        >
                            {userTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            <input
                                type="checkbox"
                                name="is_admin"
                                checked={formData.is_admin}
                                onChange={handleInputChange}
                                className="form-check-input"
                            />
                            Admin User
                        </label>
                        <p className="form-helper-text">
                            Check this box to give this user admin privileges
                        </p>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Adding User...' : 'Add User'}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <Modal form={addUserForm} setIsModalOpen={closeModal} isWide fitContent />
    );
};
